/**
 * UX do editor de Landing Pages (GrapesJS).
 *
 * Reúne os ajustes que fazem o editor ser usável por quem NÃO é desenvolvedor:
 *  1. nomes amigáveis nas Camadas ("Seção", "Botão: Garanta sua vaga") em vez de
 *     tagnames (div/span/a);
 *  2. alças de redimensionamento (os "pontos de regulagem") em qualquer bloco —
 *     largura em %, altura em min-height, sempre no dispositivo ativo;
 *  3. ajuste automático de responsividade (conserta seções "encavaladas" no
 *     celular/tablet gravando CSS dentro da media query do dispositivo);
 *  4. detecção de formulários externos (RD Station, HubSpot...) que vêm quebrados
 *     no HTML importado e não funcionam na página publicada;
 *  5. IDs legíveis em seções/formulários para o botão "Ir para o formulário".
 */
import { Editor } from 'grapesjs';

// ===========================================================================
// 1. Nomes amigáveis nas Camadas
// ===========================================================================

const TAG_LABELS: Record<string, string> = {
  section: 'Seção',
  header: 'Cabeçalho',
  footer: 'Rodapé',
  main: 'Conteúdo',
  article: 'Bloco',
  aside: 'Bloco lateral',
  nav: 'Menu',
  form: 'Formulário',
  h1: 'Título',
  h2: 'Título',
  h3: 'Subtítulo',
  h4: 'Subtítulo',
  h5: 'Subtítulo',
  h6: 'Subtítulo',
  p: 'Texto',
  span: 'Texto',
  strong: 'Texto em negrito',
  em: 'Texto em itálico',
  small: 'Texto pequeno',
  label: 'Rótulo do campo',
  button: 'Botão',
  a: 'Link',
  img: 'Imagem',
  picture: 'Imagem',
  figure: 'Imagem',
  svg: 'Ícone',
  video: 'Vídeo',
  iframe: 'Incorporado',
  ul: 'Lista',
  ol: 'Lista numerada',
  li: 'Item da lista',
  table: 'Tabela',
  tr: 'Linha da tabela',
  td: 'Célula',
  th: 'Célula do cabeçalho',
  input: 'Campo',
  textarea: 'Campo de texto',
  select: 'Campo de seleção',
  hr: 'Divisor',
  br: 'Quebra de linha',
};

const FIELD_LABELS: Record<string, string> = {
  text: 'Campo de texto',
  email: 'Campo de e-mail',
  tel: 'Campo de telefone',
  number: 'Campo numérico',
  date: 'Campo de data',
  checkbox: 'Caixa de seleção',
  radio: 'Opção',
  hidden: 'Campo oculto',
  submit: 'Botão de envio',
};

const clean = (txt: string): string =>
  (txt || '').replace(/\s+/g, ' ').trim();

const excerpt = (txt: string, max = 26): string => {
  const t = clean(txt);
  if (!t) return '';
  return t.length > max ? `${t.slice(0, max)}…` : t;
};

const looksLikeButton = (comp: any, tag: string): boolean => {
  if (tag === 'button') return true;
  const attrs = comp.getAttributes?.() || {};
  const cls = `${attrs.class || ''} ${(comp.getClasses?.() || []).join(' ')}`.toLowerCase();
  return tag === 'a' && (/\bbtn\b|button|cta/.test(cls) || !!attrs['data-action'] || !!attrs['data-btn-action']);
};

/** Nome legível de um componente, do jeito que um não-programador leria. */
export const friendlyName = (comp: any): string => {
  const tag = String(comp.get?.('tagName') || '').toLowerCase();
  const attrs = comp.getAttributes?.() || {};
  const type = String(comp.get?.('type') || '');

  if (type === 'custom-embed') return 'Código incorporado';
  if (type === 'textnode' || type === 'comment') return '';

  if (tag === 'input') {
    const t = String(attrs.type || 'text').toLowerCase();
    const base = FIELD_LABELS[t] || 'Campo';
    return attrs.name ? `${base}: ${attrs.name}` : base;
  }

  if (looksLikeButton(comp, tag)) {
    const txt = excerpt(comp.getEl?.()?.textContent || '', 20);
    return txt ? `Botão: ${txt}` : 'Botão';
  }

  if (/^h[1-6]$/.test(tag) || tag === 'p') {
    const txt = excerpt(comp.getEl?.()?.textContent || '', 24);
    const base = TAG_LABELS[tag];
    return txt ? `${base}: ${txt}` : base;
  }

  if (tag === 'div') {
    const el: HTMLElement | undefined = comp.getEl?.();
    const cls = `${attrs.class || ''}`.toLowerCase();
    if (/\bcol|coluna|column\b/.test(cls)) return 'Coluna';
    if (/\brow|linha\b/.test(cls)) return 'Linha';
    if (/\bcard|box\b/.test(cls)) return 'Card';
    if (/\bcontainer|wrap|wrapper\b/.test(cls)) return 'Container';
    try {
      if (el) {
        const parent = el.parentElement;
        const pDisplay = parent ? el.ownerDocument.defaultView?.getComputedStyle(parent).display : '';
        if (pDisplay === 'flex' || pDisplay === 'grid') return 'Coluna';
        const display = el.ownerDocument.defaultView?.getComputedStyle(el).display;
        if (display === 'flex' || display === 'grid') return 'Linha (colunas)';
      }
    } catch { /* canvas ainda montando */ }
    return 'Bloco';
  }

  if (TAG_LABELS[tag]) return TAG_LABELS[tag];
  return tag ? tag.toUpperCase() : 'Elemento';
};

/**
 * Aplica nomes amigáveis nas Camadas. Só escreve `custom-name` quando o
 * componente ainda não tem um nome definido pelo bloco/tipo custom.
 */
export const applyFriendlyNames = (editor: Editor): void => {
  const setName = (comp: any) => {
    try {
      if (!comp || comp.get('custom-name') || comp.get('name')) return;
      const name = friendlyName(comp);
      if (name) comp.set('custom-name', name, { silent: false });
    } catch { /* componente já removido */ }
  };

  const walk = (comp: any) => {
    setName(comp);
    (comp.components?.() || []).forEach((c: any) => walk(c));
  };

  // O nó raiz aparecia como "Body" (termo de dev) — vira "Página".
  const nameWrapper = () => {
    try { (editor.getWrapper() as any)?.set?.('custom-name', 'Página'); } catch { /* ignore */ }
  };
  editor.on('load', nameWrapper);
  nameWrapper();

  editor.on('component:create', setName);
  // Depois do load / setComponents os elementos existem no DOM do canvas, então
  // dá para usar o texto real ("Botão: Garanta sua vaga") e o display do pai.
  const refreshAll = () => {
    try {
      const wrapper: any = editor.getWrapper();
      (wrapper?.components?.() || []).forEach((c: any) => walk(c));
    } catch { /* editor destruído */ }
  };
  editor.on('load', () => setTimeout(refreshAll, 300));
  editor.on('component:mount', setName);
  // Renomeia enquanto o cliente edita o texto (o nome acompanha o conteúdo).
  editor.on('component:update:content', (comp: any) => {
    try {
      const tag = String(comp?.get?.('tagName') || '').toLowerCase();
      if (/^h[1-6]$/.test(tag) || tag === 'p' || looksLikeButton(comp, tag)) {
        comp.set('custom-name', friendlyName(comp));
      }
    } catch { /* ignore */ }
  });
};

/**
 * Troca a imagem de um componente de forma que a mudança REALMENTE seja salva.
 *
 * O GrapesJS exporta o `src` da PROPRIEDADE do modelo (getAttrToHTML sobrescreve
 * o atributo). Trocar só o atributo mudava a imagem na tela mas o HTML salvo
 * continuava com a imagem antiga — ao recarregar/publicar, a imagem "voltava"
 * e parecia que o upload tinha se perdido.
 */
export const applyImageSrc = (comp: any, url: string): boolean => {
  if (!comp || !url) return false;
  const src = String(url).trim();
  if (!src) return false;
  try {
    const tag = String(comp.get?.('tagName') || '').toLowerCase();
    const type = String(comp.get?.('type') || '').toLowerCase();
    const isImage = tag === 'img' || type === 'image';

    if (!isImage) {
      // Seção/div: `src` não existe nesse elemento. Antes gravávamos um atributo
      // src inútil no HTML e avisávamos "imagem atualizada" — o fundo nunca mudava.
      comp.addStyle?.({
        'background-image': `url("${src}")`,
        'background-size': 'cover',
        'background-position': 'center',
        'background-repeat': 'no-repeat'
      });
      return true;
    }

    comp.set?.('src', src);              // propriedade: é o que vai para o HTML
    comp.addAttributes?.({ src });        // atributo: mantém o DOM/preview em dia
    const el = comp.getEl?.();
    if (el && el.tagName === 'IMG') el.setAttribute('src', src);
    return true;
  } catch (e) {
    console.warn('[image] não consegui aplicar a imagem:', e);
    return false;
  }
};

/**
 * Abre os primeiros níveis da árvore de Camadas. Fechada, ela mostrava só
 * "Body" e o cliente precisava adivinhar que dava para expandir.
 */
export const expandTopLayers = (editor: Editor, levels = 2): void => {
  try {
    const layers: any = (editor as any).Layers;
    const open = (comp: any, level: number) => {
      if (!comp || level > levels) return;
      layers?.setLayerData?.(comp, { open: true });
      (comp.components?.() || []).forEach((child: any) => open(child, level + 1));
    };
    open(editor.getWrapper(), 0);
  } catch (e) {
    console.warn('[layers] não consegui abrir a árvore:', e);
  }
};

// ===========================================================================
// 1b. Blocos dos plugins em português
// ===========================================================================

// Os plugins do GrapesJS registram os blocos em inglês ("1 Column", "Text
// section", "Checkbox"). Para quem não é dev isso é ruído — aqui renomeamos
// e reagrupamos nas mesmas categorias dos nossos blocos.
const DEFAULT_BLOCK_LABELS: Record<string, { label: string; category?: string }> = {
  column1: { label: 'Uma coluna', category: 'Layout básico' },
  column2: { label: 'Duas colunas', category: 'Layout básico' },
  column3: { label: 'Três colunas', category: 'Layout básico' },
  'column3-7': { label: 'Colunas 30/70', category: 'Layout básico' },
  text: { label: 'Texto', category: 'Elementos' },
  link: { label: 'Link', category: 'Elementos' },
  image: { label: 'Imagem', category: 'Elementos' },
  video: { label: 'Vídeo', category: 'Elementos' },
  map: { label: 'Mapa', category: 'Elementos' },
  'link-block': { label: 'Bloco clicável', category: 'Elementos' },
  quote: { label: 'Citação', category: 'Elementos' },
  'text-basic': { label: 'Bloco de texto', category: 'Elementos' },
  form: { label: 'Formulário em branco', category: 'Formulários' },
  input: { label: 'Campo de texto', category: 'Formulários' },
  textarea: { label: 'Campo grande (mensagem)', category: 'Formulários' },
  select: { label: 'Lista suspensa', category: 'Formulários' },
  button: { label: 'Botão', category: 'Formulários' },
  label: { label: 'Rótulo do campo', category: 'Formulários' },
  checkbox: { label: 'Caixa de seleção', category: 'Formulários' },
  radio: { label: 'Opção (escolha única)', category: 'Formulários' },
};

export const translateDefaultBlocks = (editor: Editor): void => {
  try {
    const bm: any = (editor as any).BlockManager;
    Object.entries(DEFAULT_BLOCK_LABELS).forEach(([id, def]) => {
      const block = bm.get(id);
      if (!block) return;
      const props: any = { label: def.label };
      if (def.category) props.category = def.category;
      block.set(props);
    });
    // Re-renderiza o painel para as novas categorias agruparem corretamente.
    bm.render?.(bm.getAll?.().models);
  } catch (e) {
    console.warn('[blocks] não consegui traduzir os blocos padrão:', e);
  }
};

// ===========================================================================
// 2. Alças de redimensionamento ("pontos de regulagem")
// ===========================================================================

const NO_RESIZE_TAGS = new Set(['br', 'hr', 'script', 'style', 'body', 'html', 'option', 'tr', 'thead', 'tbody']);
const MEDIA_TAGS = new Set(['img', 'video', 'iframe', 'picture', 'svg', 'canvas']);

/**
 * Liga as alças de redimensionamento em praticamente qualquer bloco.
 *
 * - largura em **%** (não quebra o mobile como px fixo quebraria);
 * - altura em **min-height** (o conteúdo nunca é cortado);
 * - o CSS gerado entra na media query do dispositivo ativo, então dá para
 *   ajustar Desktop e Celular de forma independente.
 */
/** Media query do dispositivo ativo ('' no Desktop). */
const currentMedia = (editor: Editor): string => {
  try {
    const dm: any = (editor as any).DeviceManager;
    const device = dm?.getSelected?.() || dm?.get?.((editor as any).getDevice?.());
    return device?.get?.('widthMedia') ? `(max-width: ${device.get('widthMedia')})` : '';
  } catch { return ''; }
};

/**
 * Move para uma regra CSS "base" (sem media query) os estilos INLINE das
 * propriedades que vamos sobrescrever.
 *
 * Com `avoidInlineStyle: false` (necessário para a edição de cor vencer o lixo
 * do Word) tudo que o painel escreve vira estilo inline, e inline vence
 * qualquer regra — inclusive as de celular. Sem esta "promoção", ajustar o
 * celular não teria efeito, ou pior: escreveria inline e mudaria o desktop.
 */
const liftInlineToBaseRule = (editor: Editor, comp: any, props?: string[]): void => {
  try {
    // `get('style')` é o estilo INLINE cru (getStyle pode devolver a regra de id
    // quando o editor está em modo de regras).
    const inline: Record<string, any> = { ...(comp.get?.('style') || {}) };
    delete inline.__p;
    const alvo = props && props.length ? props : Object.keys(inline);
    const moving: Record<string, string> = {};
    const resto: Record<string, string> = {};
    Object.entries(inline).forEach(([k, v]) => {
      if (v === undefined || v === '' || v === null) return;
      if (alvo.includes(k)) moving[k] = String(v);
      else resto[k] = String(v);
    });
    if (!Object.keys(moving).length) return;
    const id = ensureCssId(comp);
    if (!id) return;
    // O valor que estava inline vira o valor BASE (desktop)...
    (editor as any).Css.setRule(`#${id}`, moving, { addStyles: true });
    // ...e sai do inline, liberando a regra do dispositivo para valer.
    comp.setStyle?.(resto, { inline: true });
  } catch (e) {
    console.warn('[style] não consegui promover o estilo inline:', e);
  }
};

/**
 * Faz a edição ser POR DISPOSITIVO de verdade.
 *
 * No Desktop o editor grava estilo inline (é o que faz a troca de cor vencer o
 * lixo de colagem do Word). Só que inline não tem media query: no celular, todo
 * ajuste vazava para o desktop e vice-versa — "arrumo o mobile e quebra o
 * desktop". Fora do Desktop passamos a gravar em REGRA (dentro da media query
 * do aparelho) e promovemos o inline existente para a regra base, para o
 * desktop continuar exatamente como estava.
 */
export const enableDeviceScopedStyles = (editor: Editor): void => {
  const aplicarEscopo = () => {
    try {
      const cfg: any = (editor as any).getConfig?.();
      if (cfg) cfg.avoidInlineStyle = !!currentMedia(editor);
    } catch { /* ignore */ }
  };
  editor.on('change:device', aplicarEscopo);
  editor.on('load', aplicarEscopo);
  aplicarEscopo();

  // Ao selecionar um bloco fora do Desktop, o que estiver inline (o valor que
  // vale hoje no desktop) vira regra base — assim o ajuste do celular tem onde
  // valer sem apagar o desktop.
  editor.on('component:selected', (comp: any) => {
    try {
      if (!currentMedia(editor)) return;
      liftInlineToBaseRule(editor, comp);
    } catch { /* ignore */ }
  });
};

export const enableResizeHandles = (editor: Editor): void => {
  // O redimensionamento SEMPRE vira regra CSS do dispositivo ativo (nunca
  // inline): é isso que faz "ajustei no celular" não bagunçar o desktop.
  editor.on('component:resize:update' as any, (opts: any) => {
    try {
      const comp = opts?.component;
      const style = opts?.style || {};
      const props = Object.keys(style);
      if (!comp || !props.length || typeof opts.updateStyle !== 'function') return;
      // Impede a escrita inline padrão do GrapesJS.
      opts.updateStyle({});
      const media = currentMedia(editor);
      // SEMPRE promove o inline, inclusive no Desktop: HTML importado (e blocos
      // nossos) trazem width/height inline, que vencem a regra CSS. Sem isto o
      // arrasto "não fazia mais nada" — o cliente puxava a alça e o tamanho
      // continuava travado no valor inline antigo.
      liftInlineToBaseRule(editor, comp, props);
      const id = ensureCssId(comp);
      if (!id) return;
      (editor as any).Css.setRule(`#${id}`, style, media
        ? { atRuleType: 'media', atRuleParams: media, addStyles: true }
        : { addStyles: true });
    } catch (e) {
      console.warn('[resize] não consegui gravar o tamanho:', e);
    }
  });

  editor.on('component:resize:init' as any, (opts: any) => {
    try {
      const comp = opts?.component;
      if (!comp) return;
      const tag = String(comp.get?.('tagName') || '').toLowerCase();
      const type = String(comp.get?.('type') || '');
      if (type === 'textnode' || type === 'comment') return;
      if (NO_RESIZE_TAGS.has(tag)) return;
      // O wrapper (corpo da página) não é redimensionável.
      if (comp === editor.getWrapper()) return;

      const common = {
        currentUnit: 0,          // usa as unidades abaixo em vez da unidade atual
        unitWidth: '%',
        unitHeight: 'px',
        step: 1,
        minDim: 16,
        keepAutoHeight: false,
        keepAutoWidth: false,
      };

      if (MEDIA_TAGS.has(tag)) {
        // Imagem/vídeo: cantos mantêm proporção, laterais ajustam livremente.
        opts.resizable = {
          ...common,
          keyWidth: 'width',
          keyHeight: 'height',
          tl: 1, tc: 0, tr: 1, cl: 1, cr: 1, bl: 1, bc: 1, br: 1,
          ratioDefault: true,
        };
        return;
      }

      // Blocos em geral (seção, div, coluna, texto, botão, formulário):
      // laterais mudam a largura, base muda a altura mínima.
      opts.resizable = {
        ...common,
        keyWidth: 'width',
        keyHeight: 'min-height',
        tl: 0, tc: 0, tr: 0, cl: 1, cr: 1, bl: 0, bc: 1, br: 1,
      };
    } catch { /* mantém o padrão do GrapesJS */ }
  });
};

// ===========================================================================
// 3. Ajuste automático de responsividade
// ===========================================================================

// Marca gravada nas regras criadas pelo ajuste — permite desfazer depois
// (inclusive numa próxima sessão, já que a marca vai junto no CSS salvo).
const FIX_MARK = '--dsk-mobile-fix';

export type ResponsiveDevice = 'Tablet' | 'Mobile';

export const MEDIA_BY_DEVICE: Record<ResponsiveDevice, string> = {
  Tablet: '(max-width: 992px)',
  Mobile: '(max-width: 768px)',
};

const canvasWin = (editor: Editor): Window | null => {
  try {
    const doc: Document | undefined = (editor as any).Canvas?.getDocument?.();
    return doc?.defaultView || null;
  } catch { return null; }
};

/** Garante um id no atributo do elemento (necessário p/ a regra CSS #id valer no HTML publicado). */
const ensureCssId = (comp: any): string | null => {
  try {
    const attrs = comp.getAttributes?.() || {};
    if (attrs.id) return String(attrs.id);
    const id = comp.getId?.();
    if (!id) return null;
    comp.addAttributes({ id });
    return id;
  } catch { return null; }
};

export interface ResponsiveFixResult {
  fixed: number;
  device: ResponsiveDevice;
  details: string[];
}

/**
 * Varre a página na largura do dispositivo e conserta o que está "encavalado":
 * posicionamento absoluto, largura fixa maior que a tela, colunas lado a lado
 * espremidas, grid com várias colunas, float e alturas fixas que cortam texto.
 *
 * Tudo é gravado como CSS **dentro da media query do dispositivo** — o desktop
 * fica intacto.
 */
export const autoFixResponsive = (editor: Editor, device: ResponsiveDevice): ResponsiveFixResult => {
  const details: string[] = [];
  let fixed = 0;
  const win = canvasWin(editor);
  const doc: Document | undefined = (editor as any).Canvas?.getDocument?.();
  const body = doc?.body;
  if (!win || !body) return { fixed: 0, device, details: ['Canvas não está pronto'] };

  const mediaText = MEDIA_BY_DEVICE[device];
  const viewport = body.clientWidth || (device === 'Mobile' ? 375 : 768);

  // Fase de ESCRITA (só roda depois de tudo medido). Escrever CSS no meio das
  // medições forçava um reflow por elemento — numa página grande isso trava o
  // navegador por vários segundos.
  const pending: { comp: any; style: Record<string, string>; why: string }[] = [];
  const queueRule = (comp: any, style: Record<string, string>, why: string) => {
    pending.push({ comp, style, why });
  };
  const addRule = (comp: any, style: Record<string, string>, why: string) => {
    const id = ensureCssId(comp);
    if (!id) return;
    // HTML importado (e nossos blocos) usam MUITO estilo inline, que vence
    // qualquer media query. Promove o valor atual para o desktop e libera a
    // regra do celular a valer de verdade.
    liftInlineToBaseRule(editor, comp, Object.keys(style));
    // atRuleParams é o nome que o CssComposer espera — com `mediaText` a regra
    // sairia FORA da media query e estragaria o desktop.
    // addStyles MESCLA: sem isso, rodar "Organizar" duas vezes apagaria as
    // correções da primeira passada (setRule troca o estilo inteiro) e a
    // página voltava a quebrar no celular.
    const rule: any = (editor as any).Css.setRule(`#${id}`, { ...style, [FIX_MARK]: '1' }, {
      atRuleType: 'media',
      atRuleParams: mediaText,
      addStyles: true,
    });
    if (rule) {
      fixed++;
      details.push(why);
    }
  };

  const all: any[] = (editor.getWrapper() as any)?.find?.('*') || [];
  // Elemento → componente: quando empilhamos uma linha de colunas precisamos
  // esticar as colunas filhas (que ficariam com os mesmos 50% de largura).
  const compByEl = new Map<HTMLElement, any>();
  all.forEach((comp) => {
    const el: HTMLElement | undefined = comp.getEl?.();
    if (el) compByEl.set(el, comp);
  });

  all.forEach((comp) => {
    const el: HTMLElement | undefined = comp.getEl?.();
    if (!el || !el.getBoundingClientRect) return;
    const tag = String(comp.get?.('tagName') || '').toLowerCase();
    if (!tag || NO_RESIZE_TAGS.has(tag) || tag === 'option') return;

    const cs = win.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    const style: Record<string, string> = {};
    const reasons: string[] = [];

    // a) Posicionamento absoluto/fixo → volta para o fluxo (fim do "encavalado")
    if (cs.position === 'absolute' || cs.position === 'fixed') {
      // Elementos decorativos de fundo (sem texto e atrás de tudo) podem ficar.
      const hasText = clean(el.textContent || '').length > 0;
      const isDecorative = !hasText && (parseInt(cs.zIndex || '0', 10) < 0 || el.children.length === 0);
      if (!isDecorative) {
        style.position = 'static';
        style.top = 'auto';
        style.left = 'auto';
        style.right = 'auto';
        style.bottom = 'auto';
        style.transform = 'none';
        reasons.push('posicionamento absoluto');
      }
    }

    // b) Largura maior que a tela / largura fixa em px
    const inlineWidth = (el.style?.width || '').trim();
    const tooWide = rect.width > viewport + 2;
    const fixedPxWidth = /px$/.test(cs.width) && parseFloat(cs.width) > viewport * 0.92;
    if ((tooWide || fixedPxWidth || /px$/.test(inlineWidth)) && tag !== 'html' && tag !== 'body') {
      style.width = '100%';
      style['max-width'] = '100%';
      reasons.push('largura maior que a tela');
    }

    // c) Altura fixa → vira mínima (texto maior no celular não é mais cortado)
    if (/px$/.test(cs.height) && el.scrollHeight > el.clientHeight + 4) {
      style.height = 'auto';
      style['min-height'] = '0';
      reasons.push('altura fixa cortando o conteúdo');
    }

    // d) Colunas lado a lado espremidas → empilha.
    // No celular, duas colunas de ~150px são ilegíveis: o padrão de mercado é
    // empilhar. Só mantemos lado a lado quando cada coluna ainda tem largura de
    // leitura confortável (ou quando é uma linha de ícones/botões sem texto).
    const kids = (Array.from(el.children) as HTMLElement[]).filter((k) => {
      const kcs = win.getComputedStyle(k);
      return kcs.display !== 'none' && kcs.position !== 'absolute' && kcs.position !== 'fixed';
    });
    if (cs.display === 'flex' && (cs.flexDirection || '').startsWith('row') && kids.length > 1) {
      const rects = kids.map((k) => k.getBoundingClientRect());
      const kidsWidth = rects.reduce((sum, kr) => sum + kr.width, 0);
      const sameLine = rects.every((kr) => Math.abs(kr.top - rects[0].top) < 8);
      const minKid = Math.min(...rects.map((kr) => kr.width));
      const comfortable = device === 'Mobile' ? 200 : 260;
      const withText = kids.some((k) => clean(k.textContent || '').length > 12);
      if (kidsWidth > rect.width + 4 || (sameLine && withText && minKid < comfortable)) {
        style['flex-direction'] = 'column';
        style['flex-wrap'] = 'wrap';
        style['align-items'] = 'stretch';
        reasons.push('colunas lado a lado espremidas');
        // Empilhar não basta: as colunas continuariam com `width: 50%`.
        // Cada uma passa a ocupar a largura toda da tela.
        kids.forEach((kid) => {
          const kidComp = compByEl.get(kid);
          if (!kidComp) return;
          const kcs = win.getComputedStyle(kid);
          const kidRect = kid.getBoundingClientRect();
          if (kidRect.width >= rect.width - 8 && kcs.flexBasis === 'auto') return; // já ocupa tudo
          queueRule(kidComp, {
            width: '100%',
            'max-width': '100%',
            'flex-basis': 'auto',
          }, `${friendlyName(kidComp)} — coluna estreita demais no celular`);
        });
      }
    }

    // e) Grid com várias colunas → uma coluna
    if (cs.display === 'grid') {
      const cols = (cs.gridTemplateColumns || '').split(' ').filter(Boolean).length;
      if (cols > 1) {
        style['grid-template-columns'] = '1fr';
        reasons.push('grid de várias colunas');
      }
    }

    // f) Float → quebra o empilhamento no celular
    if (cs.float === 'left' || cs.float === 'right') {
      style.float = 'none';
      style.width = '100%';
      reasons.push('float');
    }

    // g) Texto muito grande estourando a caixa
    if (/^h[1-6]$/.test(tag)) {
      const fs = parseFloat(cs.fontSize || '0');
      const maxFs = device === 'Mobile' ? 34 : 44;
      if (fs > maxFs) {
        style['font-size'] = `${maxFs}px`;
        style['line-height'] = '1.2';
        reasons.push('título grande demais');
      }
    }
    // Palavra comprida estourando a caixa (URL, e-mail, termo técnico). Só
    // overflow-wrap: quebra apenas o que realmente não cabe, sem picar frases.
    if (rect.width > 0 && el.scrollWidth > el.clientWidth + 4 && clean(el.textContent || '')) {
      style['overflow-wrap'] = 'break-word';
      reasons.push('palavra estourando a caixa');
    }

    if (Object.keys(style).length) {
      queueRule(comp, style, `${friendlyName(comp)} — ${reasons.join(', ')}`);
    }
  });

  // Agora sim: aplica tudo de uma vez. Se o mesmo elemento apareceu duas vezes
  // (ex.: era coluna estreita E tinha largura fixa), os estilos são somados.
  const merged = new Map();
  pending.forEach(({ comp, style, why }) => {
    const key = comp.getId?.() || comp.cid;
    const prev = merged.get(key);
    if (prev) {
      Object.assign(prev.style, style);
      if (!prev.why.includes(why)) prev.why += ` | ${why}`;
    } else {
      merged.set(key, { comp, style: { ...style }, why });
    }
  });
  merged.forEach(({ comp, style, why }) => addRule(comp, style, why));

  // Trava geral contra rolagem horizontal na página inteira.
  if (fixed > 0) {
    try {
      (editor as any).Css.setRule('body', { 'overflow-x': 'hidden', [FIX_MARK]: '1' }, {
        atRuleType: 'media', atRuleParams: mediaText, addStyles: true,
      });
    } catch { /* ignore */ }
  }

  return { fixed, device, details };
};

/**
 * Remove do CSS em texto os blocos criados pelo ajuste automático.
 *
 * Páginas HTML guardam o CSS no <head> (preservado verbatim entre sessões), e
 * não no CssComposer — sem isto, reabrir a página e clicar em "Desfazer" não
 * tiraria o ajuste da sessão anterior. Percorre o texto contando chaves, então
 * funciona também dentro de @media.
 */
export const stripFixFromCssText = (css: string): { css: string; removed: number } => {
  const src = String(css || '');
  if (!src.includes(FIX_MARK)) return { css: src, removed: 0 };
  let out = '';
  let removed = 0;
  let i = 0;
  while (i < src.length) {
    const open = src.indexOf('{', i);
    if (open === -1) { out += src.slice(i); break; }
    // Fecha o bloco correspondente respeitando aninhamento (@media).
    let depth = 1;
    let j = open + 1;
    while (j < src.length && depth > 0) {
      if (src[j] === '{') depth++;
      else if (src[j] === '}') depth--;
      j++;
    }
    const prelude = src.slice(i, open);
    const block = src.slice(open, j);
    const isAtRule = /@\w+/.test(prelude);
    if (isAtRule) {
      // Limpa por dentro e descarta a media que ficou vazia.
      const inner = stripFixFromCssText(block.slice(1, -1));
      removed += inner.removed;
      if (inner.css.trim()) out += `${prelude}{${inner.css}}`;
    } else if (block.includes(FIX_MARK)) {
      removed++;
    } else {
      out += prelude + block;
    }
    i = j;
  }
  return { css: out, removed };
};

/**
 * Limpa o ajuste automático de um pedaço de HTML (o <head> preservado das
 * páginas importadas). Mexe SÓ no conteúdo de <style> — rodar o parser de CSS
 * no HTML inteiro corromperia scripts de pixel/JSON-LD, que também têm chaves.
 */
export const stripFixFromHtmlHead = (head: string): { html: string; removed: number } => {
  const src = String(head || '');
  if (!src.includes(FIX_MARK)) return { html: src, removed: 0 };
  let removed = 0;
  const html = src.replace(/(<style\b[^>]*>)([\s\S]*?)(<\/style>)/gi, (_m, open, css, close) => {
    const res = stripFixFromCssText(css);
    removed += res.removed;
    return `${open}${res.css}${close}`;
  });
  return { html, removed };
};

/** Existe ajuste automático aplicado? (habilita o botão "Desfazer ajuste") */
export const hasResponsiveFix = (editor: Editor): boolean => {
  try {
    return ((editor as any).Css?.getRules?.() || []).some((r: any) => {
      const st = r.getStyle?.() || {};
      return !!st[FIX_MARK];
    });
  } catch { return false; }
};

/** Remove todas as regras criadas pelo ajuste automático. */
export const clearResponsiveFix = (editor: Editor, device?: ResponsiveDevice): number => {
  let removed = 0;
  try {
    const css: any = (editor as any).Css;
    const media = device ? MEDIA_BY_DEVICE[device] : null;
    const rules = [...(css.getRules?.() || [])];
    rules.forEach((r: any) => {
      const st = r.getStyle?.() || {};
      if (!st[FIX_MARK]) return;
      if (media && r.get?.('mediaText') !== media) return;
      css.remove(r);
      removed++;
    });
  } catch { /* ignore */ }
  return removed;
};

// ===========================================================================
// 4. Formulários externos que não funcionam depois de publicar
// ===========================================================================

const EXTERNAL_FORM_SIGNATURES: { re: RegExp; name: string }[] = [
  { re: /rdstation|rd-station|d335luupugsy2\.cloudfront\.net|rd_?station/i, name: 'RD Station' },
  { re: /hbspt|hsforms|hubspot/i, name: 'HubSpot' },
  { re: /activehosted|activecampaign/i, name: 'ActiveCampaign' },
  { re: /mailchimp|list-manage\.com/i, name: 'Mailchimp' },
  { re: /typeform/i, name: 'Typeform' },
  { re: /leadlovers/i, name: 'Leadlovers' },
  { re: /getresponse/i, name: 'GetResponse' },
];

export interface ExternalFormIssue {
  component: any;
  provider: string;
  reason: 'external-script' | 'empty-form';
}

/**
 * Procura formulários que só funcionam com um script do provedor original
 * (RD Station e afins) ou <form> sem nenhum campo — os dois casos publicam
 * uma caixa vazia e o cliente perde lead sem perceber.
 */
export const detectExternalForms = (editor: Editor, extraHtml = ''): ExternalFormIssue[] => {
  const issues: ExternalFormIssue[] = [];
  let workingForms = 0;
  try {
    const all: any[] = (editor.getWrapper() as any)?.find?.('*') || [];
    all.forEach((comp) => {
      const tag = String(comp.get?.('tagName') || '').toLowerCase();
      const attrs = comp.getAttributes?.() || {};
      const signature = `${attrs.id || ''} ${attrs.class || ''} ${attrs['data-provider'] || ''} ${attrs.action || ''}`;
      const match = EXTERNAL_FORM_SIGNATURES.find((s) => s.re.test(signature));
      if (match) {
        issues.push({ component: comp, provider: match.name, reason: 'external-script' });
        return;
      }
      if (tag === 'form') {
        const el: HTMLElement | undefined = comp.getEl?.();
        // Sem elemento no canvas (ainda montando) não dá para contar campos —
        // acusar aqui geraria alarme falso na hora de publicar.
        if (!el) return;
        const fields = el.querySelectorAll('input:not([type="hidden"]), textarea, select').length;
        if (fields === 0) {
          issues.push({ component: comp, provider: 'formulário sem campos', reason: 'empty-form' });
        } else {
          workingForms++;
        }
      }
    });

    // Script do provedor preservado no <head>/<body> da página importada.
    // Só vale como problema se NÃO existir nenhum formulário funcionando: senão
    // o aviso voltaria para sempre depois de trocar pelo formulário da DevSkin
    // (o <script> do RD continua no HTML, inofensivo).
    if (extraHtml && !issues.length && !workingForms) {
      const match = EXTERNAL_FORM_SIGNATURES.find((s) => s.re.test(extraHtml));
      if (match) issues.push({ component: null, provider: match.name, reason: 'external-script' });
    }
  } catch { /* ignore */ }
  return issues;
};

/**
 * Botões que apontam para uma âncora que não existe mais (a seção/formulário
 * de destino foi apagada). Clicar não faz nada e ninguém percebe até a campanha
 * já estar rodando — por isso avisamos antes de publicar.
 */
export const detectBrokenAnchors = (editor: Editor): { label: string; target: string }[] => {
  const broken: { label: string; target: string }[] = [];
  try {
    const doc: Document | undefined = (editor as any).Canvas?.getDocument?.();
    const all: any[] = (editor.getWrapper() as any)?.find?.('[data-scroll-to]') || [];
    all.forEach((comp) => {
      const target = String(comp.getAttributes?.()['data-scroll-to'] || '').trim();
      if (!target) return;
      const exists = doc ? !!doc.getElementById(target) : true;
      if (!exists) broken.push({ label: friendlyName(comp), target });
    });
  } catch { /* ignore */ }
  return broken;
};

// ===========================================================================
// 5. IDs legíveis (destino do botão "Ir para o formulário/seção")
// ===========================================================================

const slugify = (txt: string, fallback: string): string => {
  const base = clean(txt)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  return base || fallback;
};

export interface ScrollTarget {
  id: string;
  label: string;
}

/**
 * Lista os destinos possíveis para o botão "Ir para...". Atribui um id legível
 * a seções/formulários que ainda não têm — sem isso a lista aparecia vazia
 * ("Nenhuma secao com ID encontrada") e o cliente não conseguia linkar nada.
 */
export const collectScrollTargets = (editor: Editor): ScrollTarget[] => {
  const targets: ScrollTarget[] = [];
  const used = new Set<string>();
  let sectionCount = 0;
  let formCount = 0;

  // Também consulta o documento: um id novo não pode colidir com um id que já
  // existe em outro elemento (senão o botão rolaria para o lugar errado).
  const doc: Document | undefined = (editor as any).Canvas?.getDocument?.();
  const ensureId = (comp: any, preferred: string): string => {
    const attrs = comp.getAttributes?.() || {};
    let id = String(attrs.id || '').trim();
    if (!id) {
      id = preferred;
      let n = 2;
      while (used.has(id) || doc?.getElementById(id)) { id = `${preferred}-${n++}`; }
      comp.addAttributes({ id });
    }
    used.add(id);
    return id;
  };

  try {
    const all: any[] = (editor.getWrapper() as any)?.find?.('*') || [];
    all.forEach((comp) => {
      const tag = String(comp.get?.('tagName') || '').toLowerCase();
      const attrs = comp.getAttributes?.() || {};
      const compType = attrs['data-component-type'] || '';

      if (tag === 'form') {
        formCount++;
        const id = ensureId(comp, formCount === 1 ? 'formulario' : `formulario-${formCount}`);
        const title = excerpt(comp.getEl?.()?.querySelector('h1,h2,h3,legend')?.textContent || '', 24);
        targets.push({ id, label: title ? `📝 Formulário — ${title}` : `📝 Formulário${formCount > 1 ? ` ${formCount}` : ''}` });
        return;
      }

      const isSection = tag === 'section' || tag === 'header' || tag === 'footer' || compType === 'gradient-section';
      if (isSection) {
        sectionCount++;
        const heading = comp.getEl?.()?.querySelector('h1,h2,h3')?.textContent || '';
        const desc = attrs['data-section-description'] || '';
        const preferred = slugify(desc || heading, `secao-${sectionCount}`);
        const id = ensureId(comp, preferred);
        const label = excerpt(desc || heading, 30);
        targets.push({ id, label: label ? `📄 ${label}` : `📄 Seção ${sectionCount}` });
        return;
      }

      // Elementos que o cliente já nomeou manualmente também servem de âncora.
      if (attrs.id && !used.has(String(attrs.id))) {
        used.add(String(attrs.id));
        targets.push({ id: String(attrs.id), label: `# ${attrs.id}` });
      }
    });
  } catch { /* ignore */ }

  return targets;
};

/**
 * Id do primeiro formulário da página, criando um id só nele se preciso.
 * (Não usa collectScrollTargets de propósito: aquele numera a página inteira,
 * e isto roda a cada seleção de botão.)
 */
export const primaryFormId = (editor: Editor): string | null => {
  try {
    const all: any[] = (editor.getWrapper() as any)?.find?.('form') || [];
    const form = all[0];
    if (!form) return null;
    const attrs = form.getAttributes?.() || {};
    if (attrs.id) return String(attrs.id);
    const doc: Document | undefined = (editor as any).Canvas?.getDocument?.();
    let id = 'formulario';
    let n = 2;
    while (doc?.getElementById(id)) id = `formulario-${n++}`;
    form.addAttributes({ id });
    return id;
  } catch { return null; }
};
